import React, { useState } from 'react';
import './App.css';
import { SearchProvider } from './components/SearchContext';
import SearchContainer from './components/SearchContainer';
import AdvancedSearchContainer from './components/AdvancedSearchContainer';
import { LocalStorageDataService } from './services/LocalStorageDataService';
import { CSVDataLoaderImpl } from './services/CSVDataLoader';
import Header from './components/Header';
import Footer from './components/Footer';
import useToast from './components/ToastContainer';
import LoadingSpinner from './components/LoadingSpinner';
import DataMigrationPanel from './components/DataMigrationPanel';

function App() {
  const [activeView, setActiveView] = useState<'basic' | 'advanced'>('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [showMigrationPanel, setShowMigrationPanel] = useState(false);
  const { addToast, ToastContainer } = useToast();

  const handleViewChange = (view: 'basic' | 'advanced') => {
    setActiveView(view);
  };

  const toggleMigrationPanel = () => {
    setShowMigrationPanel(!showMigrationPanel);
  };

  return (
    <div className="App">
      <Header activeView={activeView} onViewChange={handleViewChange} />
      
      <main>
        <div className="admin-controls">
          <button 
            className="admin-button"
            onClick={toggleMigrationPanel}
          >
            {showMigrationPanel ? 'データマイグレーションパネルを閉じる' : 'データマイグレーションパネルを開く'}
          </button>
        </div>
        
        {showMigrationPanel && (
          <DataMigrationPanel />
        )}
        
        {isLoading ? (
          <LoadingSpinner size="large" message="データを読み込んでいます..." />
        ) : (
          <SearchProvider>
            {activeView === 'basic' ? (
              <SearchContainer />
            ) : (
              <AdvancedSearchContainer />
            )}
          </SearchProvider>
        )}
      </main>
      
      <Footer />
      <ToastContainer />
    </div>
  );
}

// CSVデータをロードするためのデモ機能
const loadDemoData = async () => {
  const dataService = new LocalStorageDataService();
  const csvLoader = new CSVDataLoaderImpl();
  
  // デモ用のコンペティションCSVデータ
  const competitionsCsv = `Slug,Title,Subtitle,DeadlineDate,Tags,DataDomain,ParticipantsCount
titanic,Titanic - Machine Learning from Disaster,Predict survival on the Titanic,2023-12-31,binary classification|tabular data|beginner,social science,15000
house-prices,House Prices - Advanced Regression Techniques,Predict sales prices and practice feature engineering,2023-11-30,regression|tabular data|intermediate,economics,10000
digit-recognizer,Digit Recognizer,Learn computer vision fundamentals with the famous MNIST data,2023-10-31,multiclass classification|computer vision|beginner,computer vision,8000
nlp-getting-started,Natural Language Processing with Disaster Tweets,Predict which Tweets are about real disasters and which ones are not,2023-09-30,nlp|text classification|intermediate,natural language processing,5000
store-sales-time-series-forecasting,Store Sales - Time Series Forecasting,Use machine learning to predict grocery sales,2023-08-31,time series|forecasting|regression,retail,7500`;

  // デモ用のソリューションCSVデータ
  const solutionsCsv = `CompetitionSlug,Rank,Score,UserIds,Summary
titanic,1,0.95,user1|user2,Ensemble of XGBoost and LightGBM with feature engineering
titanic,2,0.93,user3,Neural network approach with categorical embeddings
house-prices,1,0.88,user1,Stacked regression models with feature selection
digit-recognizer,1,0.99,user2,Convolutional Neural Network with data augmentation
nlp-getting-started,1,0.85,user3,BERT-based model with custom preprocessing
store-sales-time-series-forecasting,1,0.79,user1|user3,Prophet model with external regressors`;

  // デモ用のユーザーCSVデータ
  const usersCsv = `UserId,CurrentRank,HighestRank
user1,100,50
user2,200,150
user3,150,100`;

  try {
    // CSVデータをロード
    const competitions = await csvLoader.loadCompetitionsFromCSV(competitionsCsv);
    const solutions = await csvLoader.loadSolutionsFromCSV(solutionsCsv);
    const users = await csvLoader.loadUsersFromCSV(usersCsv);
    
    // データサービスに保存
    await dataService.updateData({
      competitions,
      solutions,
      users
    });
    
    console.log('デモデータがロードされました');
  } catch (error) {
    console.error('デモデータのロード中にエラーが発生しました:', error);
  }
};

// アプリケーション起動時にデモデータをロード
loadDemoData();

export default App;
